import React, { useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Box, Flex, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { CypressIds } from '../../constants/cypressIds';
import { RequirementTemplatesDropdownDuskIds } from '../../constants/duskIds';
import { useRequirementTemplatesData } from '../../services/requirement-template-service';
import { getInfiniteListData } from '../../services/util';
import colors from '../../styles/theme/foundation/colors';
import { buttonItemStyle } from '../../styles/theme/util/popoverMenuButtonStyles';
import LoadingSpinner from '../Spinner';

import useDebouncedValue from './util';

const TemplateContent = ({
  companyId,
  searchInput,
  folderId,
  handleInputChange
}) => {
  const sort = 'name';
  const debouncedSearch = useDebouncedValue(searchInput, 300);

  const filter = useMemo(() => ({
    creator_id: [],
    name: debouncedSearch,
    published: true,
    requirement_template_folder_id: folderId
  }), [debouncedSearch, folderId]);

  const { data,
    isLoading,
    fetchNextPage,
    hasNextPage } = useRequirementTemplatesData({ companyId, folderId, filter, sort });

  const templateData = useMemo(() => {
    if (isLoading) {
      return [];
    }

    return getInfiniteListData(data);
  }, [data, isLoading]);

  if (isLoading) {
    return <LoadingSpinner isInline minHeight="200px" />;
  }

  if (templateData.length) {
    return (
      <Box className="templates-nested-dropdown-menu">
        <InfiniteScroll
          dataLength={templateData.length}
          hasMore={hasNextPage}
          height="200px"
          loader={(<LoadingSpinner isInline />)}
          next={fetchNextPage}
          scrollableTarget="templates-nested-dropdown-menu"
        >
          {templateData.map(template => (
            <Flex
              _hover={buttonItemStyle.hover}
              data-cy={CypressIds.reqTemplateGetTemplateOptionByName(template.name)}
              dusk={RequirementTemplatesDropdownDuskIds.getTemplateOptionByName(template.name)}
              id={template.id}
              key={template.id}
              style={{
                ...buttonItemStyle,
                color: colors.greyScale['900'],
                alignItems: 'flex-start',
                marginBottom: '0.5rem',
                maxHeight: '100px',
                height: 'auto',
                flexDirection: 'column',
                width: '100%',
                whiteSpace: 'normal'
              }}
              value={template.id}
              variant="unstyled"
              onClick={() => handleInputChange(template)}
            >
              <Text pointerEvents="none">{template.name}</Text>
              <Box
                maxHeight="32px"
                overflow="hidden"
                pointerEvents="none"
              >
                <Text
                  color={colors.greyScale['500']}
                  fontStyle={!template.description && 'italics'}
                  id="template-description"
                  textStyle="bodyXs"
                >
                  {template.description || 'No Description'}
                </Text>
              </Box>

            </Flex>
          ))}
        </InfiniteScroll>
      </Box>
    );
  }

  return <Text color={colors.greyScale['500']} my={2} textStyle="bodyMd">No templates</Text>;
};

TemplateContent.propTypes = {
  companyId: PropTypes.number,
  folderId: PropTypes.number,
  searchInput: PropTypes.string,
  handleInputChange: PropTypes.func
};

export default TemplateContent;
